import { Button, Col, DatePicker, Form, Row, TimePicker } from 'antd';
import classNames from 'classnames';
import {
  get,
  groupBy,
  isArray,
  isBoolean,
  isEmpty,
  isNil,
  last,
  set,
} from 'lodash-es';
import moment from 'moment';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  getAdjustedDateRangeValues,
  isDateRangeEqual,
} from '../../common/utils/dateRangeUtils';
import { joinDateAndTime, now, toDate } from '../../common/utils/dateUtils';
import { SingleDateField } from '../dateRange/SingleDateField';
import { useDateRangePresets } from '../../config/dateRangeConstants';
import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import {
  DateFormatNumeric,
  timeFormatShort,
  useDateFormats,
} from '../../utils/dateFormatting';
import { makeComposedFormAccessors } from '../../utils/reactUtils';
import { FAIcon, FATypes } from '../adapters/fontAwesomeAdapters';
import { Popup, PopupContext } from '../nav/navElements';
import { FormItem } from './FormItem';
import { FormItemInputHidden } from './basicFormElements';
import { getIsRequired } from './formHelpers';
import { useFormContext } from './forms';
import { useDateElementsStyles } from './dateElements.styles';
import { useLayoutElementsStyles } from '../layout/layoutElements.styles';

const CalendarIcon = ({ className, ...rest }) => {
  const { styles } = useDateElementsStyles();

  return (
    <FAIcon
      {...rest}
      className={classNames(
        styles.datePreviewIcon,
        'DatePreviewIcon',
        className
      )}
      icon="calendar"
      type={FATypes.REGULAR}
    />
  );
};
const ClockIcon = ({ className, ...rest }) => {
  const { styles } = useDateElementsStyles();

  return (
    <FAIcon
      {...rest}
      className={classNames(
        styles.datePreviewIcon,
        'DatePreviewIcon',
        className
      )}
      icon="clock"
      type={FATypes.REGULAR}
    />
  );
};

function makePlaceholder({
  placeholder,
  isRequired,
  isFloatingLabel,
  defaultPlaceholder,
}) {
  const pl = placeholder || defaultPlaceholder;
  return isRequired && !isFloatingLabel ? `${pl} *` : pl;
}

function DatePickerAdapter({
  className,
  placeholder,
  isRequired,
  isFloatingLabel,
  showIcon = true,
  ...rest
}) {
  const { styles } = useDateElementsStyles();
  const { numeric: dateFormatNumeric } = useDateFormats();

  return (
    <DatePicker
      className={classNames(showIcon && styles.suffixAsPrefix, className)}
      format={dateFormatNumeric}
      placeholder={makePlaceholder({
        placeholder,
        isRequired,
        isFloatingLabel,
        defaultPlaceholder: dateFormatNumeric,
      })}
      suffixIcon={showIcon ? <CalendarIcon /> : null}
      showTime={false}
      {...rest}
    />
  );
}

function TimePickerAdapter({
  className,
  placeholder,
  isRequired,
  isFloatingLabel,
  showIcon = true,
  minuteStep = 15,
  ...rest
}) {
  const { styles } = useDateElementsStyles();

  return (
    <TimePicker
      className={classNames(styles.suffixAsPrefix, className)}
      format={timeFormatShort}
      placeholder={makePlaceholder({
        placeholder,
        isRequired,
        isFloatingLabel,
        defaultPlaceholder: timeFormatShort,
      })}
      suffixIcon={showIcon ? <ClockIcon /> : null}
      minuteStep={minuteStep}
      {...rest}
    />
  );
}

export function FormItemDatePicker({
  showIcon,
  formItemComponentProps,
  rules,
  floatingLabel = true,
  ...rest
}) {
  const parseValue = val => (isNil(val) ? val : toDate(val));

  return (
    <FormItem
      {...rest}
      component={DatePickerAdapter}
      validateTrigger={['onBlur', 'onChange']}
      formItemComponentProps={{
        parseValue,
        showIcon,
        isRequired: getIsRequired(rules),
        isFloatingLabel: floatingLabel,
        ...formItemComponentProps,
      }}
      rules={rules}
      floatingLabel={floatingLabel}
    />
  );
}

export function FormItemTimePicker({
  showIcon,
  formItemComponentProps,
  rules,
  floatingLabel = true,
  ...rest
}) {
  return (
    <FormItem
      {...rest}
      component={TimePickerAdapter}
      validateTrigger={['onBlur', 'onChange']}
      formItemComponentProps={{
        showIcon,
        isRequired: getIsRequired(rules),
        isFloatingLabel: floatingLabel,
        ...formItemComponentProps,
      }}
      rules={rules}
      floatingLabel={floatingLabel}
    />
  );
}

function ErrorSetter({ form, setHasError, name }) {
  // This needs to be in effect, therefore a simple function cannot be used
  useEffect(() => {
    setHasError(!isEmpty(form.getFieldError(name)));
  });
  return null;
}

const addSuffixToName = (name, suffix) =>
  isArray(name)
    ? [...name.slice(0, -1), `${last(name)}${suffix}`]
    : `${name}${suffix}`;

function syncValue({ form, sourceValue, targetValue, targetName, isEqual }) {
  const shouldSync =
    (sourceValue && !targetValue) ||
    (!sourceValue && targetValue) ||
    (sourceValue && targetValue && !isEqual(sourceValue, targetValue));

  if (shouldSync) {
    const newValues = {};
    set(newValues, targetName, sourceValue);
    form.setFieldsValue(newValues);
  }
}

export function FormItemDateTimePicker({
  dateProps,
  timeProps,
  rules,
  name,
  disabled,
  expandSize,
  normalize,
  floatingLabel = false,
  ...rest
}) {
  const { formInstance: form, values, forceUpdate } = useFormContext();
  const { separateRules = [], groupRules = [] } = groupBy(rules || [], rule =>
    isBoolean(rule.required) ? 'separateRules' : 'groupRules'
  );

  const [hasError, setHasError] = useState(false);
  const errorProps = hasError ? { validateStatus: 'error' } : {};

  const [pickerOpen, setPickerOpen] = useState();

  const updateComposedField = useCallback(() => {
    const date = form.getFieldValue(addSuffixToName(name, '-date'));
    const time = form.getFieldValue(addSuffixToName(name, '-time'));
    const dateTime = date && time ? joinDateAndTime(date, time) : undefined;

    if (dateTime) {
      // Virtual date-time field will be touched the first time it has value
      form.setFields([{ name, touched: true }]);
    }
    const newValues = {};
    set(newValues, name, normalize ? normalize(dateTime) : dateTime);
    form.setFieldsValue(newValues);
    forceUpdate();
    if (form.isFieldTouched(name)) {
      form.validateFields([name]);
    }
  }, [forceUpdate, form, name, normalize]);

  // Synchronize the main value and partial values (for example if set from outside)
  const value = get(values, name);
  useEffect(() => {
    const dateValue = form.getFieldValue(addSuffixToName(name, '-date'));
    const timeValue = form.getFieldValue(addSuffixToName(name, '-time'));

    // If entering is in progress (one of the two subfields is entered), we don't want to sync
    const ignoreSync = (dateValue && !timeValue) || (!dateValue && timeValue);
    if (ignoreSync) {
      return;
    }
    syncValue({
      form,
      sourceValue: value,
      targetValue: dateValue,
      targetName: addSuffixToName(name, '-date'),
      isEqual: (m1, m2) => m1.isSame(m2, 'day'),
    });
    syncValue({
      form,
      sourceValue: value,
      targetValue: timeValue,
      targetName: addSuffixToName(name, '-time'),
      isEqual: (m1, m2) => {
        const time1 = joinDateAndTime(moment(), m1);
        const time2 = joinDateAndTime(moment(), m2);
        return time1.isSame(time2, 'minute');
      },
    });
  }, [form, name, value]);

  const setNow = useCallback(() => {
    const dateTime = now();
    const newValues = {};
    set(newValues, name, normalize ? normalize(dateTime) : dateTime);
    set(newValues, addSuffixToName(name, '-date'), dateTime);
    set(newValues, addSuffixToName(name, '-time'), dateTime);
    form.setFieldsValue(newValues);
    forceUpdate();
    setTimeout(() => setPickerOpen(false), 0);
  }, [forceUpdate, form, name, normalize]);

  const setNowTime = useCallback(() => {
    const currentTime = now();
    const newValues = {};
    set(newValues, addSuffixToName(name, '-time'), currentTime);
    form.setFieldsValue(newValues);
    updateComposedField();
    document.activeElement?.blur();
  }, [form, name, updateComposedField]);

  const { styles } = useDateElementsStyles();

  const dateFormItemComponentProps = {
    onChange: updateComposedField,
    showToday: false,
    ...dateProps?.formItemComponentProps,
    classNames: {
      popup: {
        root: classNames(dateProps?.formItemComponentProps?.popupClassName, {
          [styles.allowPointerEvents]:
            dateProps?.formItemComponentProps?.cellRender,
        }),
      },
    },
    renderExtraFooter: () => (
      <>
        {dateProps?.showNow !== false && (
          <div className={styles.footerButton} onClick={setNow}>
            <FormattedMessage id="date.now" />
          </div>
        )}
        {dateProps?.formItemComponentProps?.renderExtraFooter &&
          dateProps.formItemComponentProps.renderExtraFooter({
            pickerOpen,
            setPickerOpen,
          })}
      </>
    ),
    open: pickerOpen,
    onOpenChange: setPickerOpen,
  };
  const datePropsRules = dateProps?.rules || [];
  return (
    <div
      className={classNames('DateTimePicker', styles.dateTimePicker, {
        'expand-size': expandSize,
      })}
    >
      <div className="Col-DatePicker">
        <FormItemDatePicker
          name={addSuffixToName(name, '-date')}
          floatingLabel={floatingLabel}
          disabled={disabled}
          {...dateProps}
          rules={[...separateRules, ...datePropsRules]}
          formItemComponentProps={{
            ...dateFormItemComponentProps,
            onChange: (...args) => {
              updateComposedField(...args);
              dateFormItemComponentProps?.onChange(...args);
            },
          }}
          {...errorProps}
        />
        <FormItemInputHidden
          name={name}
          rules={groupRules}
          className={styles.validationGroup}
          {...rest}
        />
        <Form.Item noStyle shouldUpdate>
          {fm => (
            <ErrorSetter form={fm} name={name} setHasError={setHasError} />
          )}
        </Form.Item>
      </div>
      <div className="Col-TimePicker">
        <FormItemTimePicker
          name={addSuffixToName(name, '-time')}
          floatingLabel={floatingLabel}
          rules={separateRules}
          disabled={disabled}
          {...timeProps}
          formItemComponentProps={{
            onChange: updateComposedField,
            showNow: false,
            classNames: {
              popup: {
                root: styles.timePickerWithNow,
              },
            },
            renderExtraFooter: () => (
              <div className={styles.footerButton} onClick={setNowTime}>
                <FormattedMessage id="date.now" />
              </div>
            ),
            ...timeProps?.formItemComponentProps,
          }}
          {...errorProps}
        />
      </div>
    </div>
  );
}

function DateRangePreview({ value, placeholder }) {
  const { from, to } = value || {};
  const isValueEmpty = !from && !to;
  const { styles } = useDateElementsStyles();

  return (
    <Row
      align="middle"
      style={{ display: 'inline-flex' }}
      className={classNames('flex-nowrap', styles.dateRangePreview)}
    >
      <Col>
        <CalendarIcon />
      </Col>
      {isValueEmpty ? (
        <Col>{placeholder}</Col>
      ) : (
        <>
          <Col>
            <DateFormatNumeric value={from} />
          </Col>
          <Col style={{ fontWeight: 'normal' }}>
            <FormattedMessage id="filters.labels.rangeJoin" />
          </Col>
          <Col>
            <DateFormatNumeric value={to} />
          </Col>
        </>
      )}
    </Row>
  );
}

const DEFAULT_POPUP_PROPS = {};

const ColBottomRow = ({ children, ...rest }) => (
  <Col {...rest}>
    <Form.Item>{children}</Form.Item>
  </Col>
);

function DateRangePresetButton({ name, selected, value, labelId, setValue }) {
  const { setOpen } = useContext(PopupContext);
  const { styles } = useDateElementsStyles();

  const onClick = () => {
    setValue(name, value);
    setOpen(false);
  };

  return (
    <Button
      className={styles.dateRangePresetButton}
      type={selected ? 'primary' : 'default'}
      onClick={onClick}
    >
      <FormattedMessage id={labelId} />
    </Button>
  );
}
function PopupAutocloser({ value }) {
  const { setOpen } = useContext(PopupContext);
  const from = value?.from;
  const to = value?.to;

  const prevTo = useRef(to);
  useEffect(() => {
    // Close if "to" changed and full range is entered
    if (to && (!prevTo.current || !to.isSame(prevTo.current, 'day')) && from) {
      setOpen(false);
    }
    prevTo.current = to;
  }, [from, setOpen, to]);

  return null;
}

// Edits inside the popup mutate a local draft and are only committed to the
// form (which drives the filtered list) when the popup closes — clicking
// outside, picking a preset, or auto-closing on a complete range.
function DateRangePopupContent({
  committedValue,
  onCommit,
  presetsOrdered,
  presetsByKey,
}) {
  const { isOpen } = useContext(PopupContext);
  const [draft, setDraft] = useState(committedValue);
  const { styles } = useDateElementsStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const {
    from: fromProps,
    to: toProps,
    namedRange,
  } = makeComposedFormAccessors({
    value: draft,
    onChange: setDraft,
    fields: ['from', 'to', 'namedRange'],
  });

  // Always hold the latest draft so the commit-on-close effect can read it
  // without depending on `draft` (which would re-run it on every keystroke).
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const wasOpenRef = useRef(isOpen);
  // Track the committed value we last seeded from, so we can tell an external
  // change (clear button, "clear all filters") apart from the user's own
  // in-popup edits — only the former should re-seed the draft.
  const seededFromRef = useRef(committedValue);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    const justClosed = !isOpen && wasOpenRef.current;
    const externallyChanged = !isDateRangeEqual(
      committedValue,
      seededFromRef.current
    );

    // Commit the buffered draft to the form (which drives the list) on close.
    if (justClosed) {
      onCommit(draftRef.current);
    }

    // Seed the draft on open, and re-seed if the committed value changes
    // externally while open, so a stale draft can't be re-committed on close
    // and silently undo the external change. Skip the redundant update when the
    // draft already matches — re-rendering on open makes the popup reposition.
    if (isOpen && (justOpened || externallyChanged)) {
      if (!isDateRangeEqual(committedValue, draftRef.current)) {
        setDraft(committedValue);
      }
      seededFromRef.current = committedValue;
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, committedValue, onCommit]);

  // Clear `namedRange` when the draft no longer matches its preset
  useEffect(() => {
    const preset = presetsByKey[namedRange?.value];
    if (preset && !isDateRangeEqual(preset, draft)) {
      namedRange.onChange(null);
    }
  }, [namedRange, presetsByKey, draft]);

  const setPresetValue = (name, val) => {
    setDraft(
      val && {
        namedRange: name,
        ...val,
      }
    );
  };

  const applyField = (fieldName, parsed) => {
    const updated = getAdjustedDateRangeValues(
      fieldName,
      parsed,
      { from: fromProps.value, to: toProps.value },
      { startField: 'from', endField: 'to' }
    );
    setDraft({ ...draft, ...updated });
  };

  return (
    <>
      <PopupAutocloser value={draft} />
      <Row
        align="bottom"
        className={classNames(
          styles.dateRangeContainer,
          'no-margin-form-items'
        )}
        gutter={pxToNumber(cssVariables.spaceSm)}
      >
        <Col className={layoutStyles.flex1}>
          <SingleDateField
            value={fromProps.value}
            onChange={parsed => applyField('from', parsed)}
          />
        </Col>
        <ColBottomRow style={{ textAlign: 'center' }}>
          <span className={styles.dateRangeDash}>-</span>
        </ColBottomRow>
        <Col className={layoutStyles.flex1}>
          <SingleDateField
            value={toProps.value}
            onChange={parsed => applyField('to', parsed)}
          />
        </Col>
      </Row>
      {presetsOrdered.map(({ name, labelId, value: val }) => (
        <DateRangePresetButton
          key={name}
          name={name}
          labelId={labelId}
          value={val}
          setValue={setPresetValue}
          selected={namedRange.value === name}
        />
      ))}
    </>
  );
}

function DateRangePopup({
  popupComponent: PopupC = Popup,
  popupProps,
  value,
  onChange,
  placeholder,
}) {
  const { presetsOrdered, presetsByKey } = useDateRangePresets();

  return (
    <PopupC
      label={<DateRangePreview value={value} placeholder={placeholder} />}
      {...DEFAULT_POPUP_PROPS}
      {...popupProps}
    >
      <DateRangePopupContent
        committedValue={value}
        onCommit={onChange}
        presetsOrdered={presetsOrdered}
        presetsByKey={presetsByKey}
      />
    </PopupC>
  );
}

export function FormItemDateRangePopup({
  popupComponent,
  popupProps,
  formItemComponentProps,
  ...rest
}) {
  return (
    <FormItem
      {...rest}
      floatingLabel={false}
      component={DateRangePopup}
      formItemComponentProps={{
        ...formItemComponentProps,
        popupComponent,
        popupProps,
      }}
    />
  );
}
